import { create } from "zustand";
import {
  getPreassessment,
  getPreassessmentForce,
  PreassessmentData,
  sendPreassessmentAnswer,
} from "../axios/quiz";

export interface Answer {
  concept: string;
  score: number;
  all_score: number;
}

interface QuizeStore {
  preassessmentData: PreassessmentData;
  loading: boolean;
  error: string | null;
  previousModalClosed: boolean;
  needCloseButton: boolean;
  fetchPreassessment: (opts?: { preserveScores?: boolean }) => Promise<void>;
  fetchPreassessmentForce: () => Promise<void>;
  sendAnswer: (answer: Answer) => Promise<void>;
  clearStore: () => Promise<void>;
  setPreviousModalClosed: (value: boolean) => void;
  toggleNeedCloseButton: () => void;
}

export const useQuizeStore = create<QuizeStore>((set, get) => ({
  preassessmentData: {
    questions: [],
    total_questions_number: 0,
    answered_questions_number: 0,
    scores_earned: 0,
  },
  loading: false,
  error: null,
  previousModalClosed: true,
  needCloseButton: false,

  fetchPreassessment: async (opts?: { preserveScores?: boolean }) => {
    set({ loading: true, error: null });
    try {
      const fresh = await getPreassessment();
      set((state) => {
        const merge: PreassessmentData =
          opts?.preserveScores && state.preassessmentData
            ? {
                ...fresh,
                answered_questions_number: Math.max(
                  fresh.answered_questions_number,
                  state.preassessmentData.answered_questions_number
                ),
                scores_earned: Math.max(
                  fresh.scores_earned,
                  state.preassessmentData.scores_earned
                ),
              }
            : fresh;
        return { preassessmentData: merge, loading: false };
      });
    } catch (error: any) {
      console.error("Error fetching preassessment:", error);
      set({ error: "Failed to load preassessment questions", loading: false });
    }
  },

  fetchPreassessmentForce: async () => {
    set({ loading: true, error: null });
    try {
      const preassessmentData = await getPreassessmentForce();
      set({ preassessmentData, loading: false });
    } catch (error: any) {
      console.error("Error fetching preassessment (force):", error);
      set({
        error: "Failed to load preassessment questions (force)",
        loading: false,
      });
    }
  },

  sendAnswer: async (answer: Answer) => {
    try {
      await sendPreassessmentAnswer({ answers: [answer] });
      set((state) => ({
        preassessmentData: {
          ...state.preassessmentData,
          answered_questions_number:
            state.preassessmentData.answered_questions_number + 1,
          scores_earned: state.preassessmentData.scores_earned + answer.score,
        },
      }));
    } catch (error) {
      console.error("Error sending preassessment answer:", error);
      throw error;
    }
  },

  clearStore: async () =>
    set({
      preassessmentData: {
        questions: [],
        total_questions_number: 0,
        answered_questions_number: 0,
        scores_earned: 0,
      },
    }),

  setPreviousModalClosed: (value: boolean) =>
    set({ previousModalClosed: value }),

  toggleNeedCloseButton: () =>
    set((state) => ({ needCloseButton: !state.needCloseButton })),
}));
